import { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Send, LogIn } from "lucide-react";
import { toast } from "sonner";
import { useAuth, api, goToLogin } from "@/contexts/AuthContext";

export default function ContactSheet({ open, onOpenChange, person }) {
  const { user, loading } = useAuth();
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!person) return null;

  const isDemo = person.isDemo === true;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      goToLogin(`/people/${person.id}`);
      return;
    }
    if (!message.trim()) {
      toast.error("Add a short message before sending.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/enquiries", {
        to_user_id: person.id,
        message: message.trim(),
      });
      toast.success(`Request sent to ${person.name}.`, {
        description: "They'll get back to you by email.",
      });
      setMessage("");
      onOpenChange(false);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't send request.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md bg-white border-l border-[#F3F4F6] p-8 overflow-y-auto"
      >
        <SheetHeader className="text-left">
          <SheetTitle className="font-display text-2xl text-[#111827]">
            Request help from {person.name.split(" ")[0]}
          </SheetTitle>
          <SheetDescription className="text-[#4B5563] font-body">
            {isDemo
              ? "This is a demo profile — messaging is disabled."
              : "Send an enquiry describing what you're trying to solve."}
          </SheetDescription>
        </SheetHeader>

        {isDemo ? (
          <div className="mt-8 rounded-2xl bg-[#EBFFF5] ring-1 ring-[#B8FFDD] p-5 text-sm text-[#B03A3A]">
            Demo profiles are here to show what a real solver page looks like.
            You can still browse — messaging opens once real solvers join.
          </div>
        ) : !user && !loading ? (
          <div className="mt-8 space-y-4">
            <p className="text-sm text-[#4B5563]">
              Sign in to send a request. It takes one click.
            </p>
            <Button
              onClick={() => goToLogin(`/people/${person.id}`)}
              data-testid="contact-signin"
              className="w-full h-12 rounded-full bg-[#111827] hover:bg-black text-white font-semibold"
            >
              <LogIn size={16} className="mr-2" />
              Continue with Google
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="mt-8 space-y-4">
            <div>
              <Label htmlFor="contact-message" className="text-sm text-[#4B5563]">
                Your message
              </Label>
              <Textarea
                id="contact-message"
                data-testid="contact-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={`Hi ${person.name.split(" ")[0]}, I'd love your help with…`}
                className="mt-1.5 min-h-32 rounded-xl"
              />
              <p className="mt-2 text-xs text-[#9CA3AF]">
                Sending as {user?.name} · {user?.email}
              </p>
            </div>
            <Button
              type="submit"
              data-testid="contact-submit"
              disabled={submitting}
              className="w-full h-12 rounded-full bg-[#3FFFB6] hover:bg-[#2DE59F] text-[#111827] font-semibold text-base transition-colors"
            >
              <Send size={16} className="mr-2" />
              {submitting ? "Sending…" : "Send request"}
            </Button>
          </form>
        )}
      </SheetContent>
    </Sheet>
  );
}
